import React, { useEffect, useState } from "react";
import { TbArrowLeft, TbArrowRight, TbInfoCircleFilled } from "react-icons/tb";
import { useNavigate, useOutletContext } from "react-router-dom";
import styled from "styled-components";
import Button from "../../components/Button";
import { Body, Subhead, Title1 } from "../../styles/typography";
import AuthorNameFields, { MAX_NAME_LENGTH } from "./steps/AuthorNameFields";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, userActions } from "../../app/reducers/UserSlice";

const PageContainer = styled.div`
  margin: 0 auto;
  margin-top: 90px;
  padding: 24px;
  background: rgb(${(p) => p.theme.colors.white});
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled(Body)`
  color: rgb(${(p) => p.theme.colors.neutral400});
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;

  button {
    width: fit-content;
  }
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgb(${(p) => p.theme.colors.error600});
`;

const AuthorNameEdit = () => {
  const navigate = useNavigate();
  const { bookDetails, updateBookDetails } = useOutletContext();
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(userActions.principal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const user = useSelector(selectUser);

  const [draft, setDraft] = useState({
    firstName: bookDetails.firstName ?? "",
    lastName: bookDetails.lastName ?? "",
  });

  const hasSeededFromAccount = React.useRef(false);
  useEffect(() => {
    if (hasSeededFromAccount.current || !user) return;
    hasSeededFromAccount.current = true;
    if (!bookDetails.firstName && !bookDetails.lastName) {
      setDraft((prev) => ({
        firstName: prev.firstName || user?.name?.first || "",
        lastName: prev.lastName || user?.name?.last || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isFirstNameEmpty = draft.firstName.trim() === "";
  const isLastNameEmpty = draft.lastName.trim() === "";
  const isFirstNameOverLimit = draft.firstName.length > MAX_NAME_LENGTH;
  const isLastNameOverLimit = draft.lastName.length > MAX_NAME_LENGTH;
  const isComplete =
    !isFirstNameEmpty &&
    !isLastNameEmpty &&
    !isFirstNameOverLimit &&
    !isLastNameOverLimit;
  const showError = hasAttemptedSave && !isComplete;
  const errorMessage =
    isFirstNameEmpty || isLastNameEmpty
      ? "Please complete both name fields to continue."
      : `Please keep first and last name to ${MAX_NAME_LENGTH} characters or fewer.`;

  const handleChange = (next) => {
    setDraft({ firstName: next.firstName, lastName: next.lastName });
  };

  const handleSave = () => {
    if (!isComplete) {
      setHasAttemptedSave(true);
      return;
    }
    updateBookDetails((prev) => ({
      ...prev,
      firstName: draft.firstName,
      lastName: draft.lastName,
    }));
    navigate("/book/review");
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Heading>
          <Title1 as="h2">Who is the author of this book? (Required)</Title1>
          <Description>
            This is the name that will appear as the book&apos;s author. It
            doesn&apos;t need to match the name on this account.
          </Description>
        </Heading>

        <AuthorNameFields
          required
          value={draft}
          onChange={handleChange}
          showError={hasAttemptedSave}
        />

        {showError && (
          <ErrorRow>
            <TbInfoCircleFilled size={20} />
            <Subhead>{errorMessage}</Subhead>
          </ErrorRow>
        )}

        <ButtonRow>
          <Button
            variant="tertiary"
            onButtonClick={() => navigate("/book/review")}
          >
            <TbArrowLeft /> Back
          </Button>
          <Button variant="primary" onButtonClick={handleSave}>
            Save &amp; return to review <TbArrowRight />
          </Button>
        </ButtonRow>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AuthorNameEdit;
